import React, { useEffect, useState, useCallback } from 'react';
import { Seo } from '../components/Seo';
import axios from 'axios';
import { Link, useNavigate, NavLink } from 'react-router-dom';
import { toast } from 'sonner';
import { useJuryAuth, formatApiErrorDetail } from '@/context/JuryAuthContext';
import usePageTitle from '@/hooks/usePageTitle';
import { ScrollText, Tags, LogOut, Clock, ArrowRight, CheckCircle2, Circle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ withCredentials: true });

const fmtPercent = (a, b) => (!b ? 0 : Math.round((a / b) * 100));

const JuryShell = ({ children, active }) => {
  const { juror, logout } = useJuryAuth();
  const navigate = useNavigate();
  const doLogout = async () => { await logout(); navigate('/jury-chamber/login', { replace: true }); };

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--ink)] flex" data-testid="jury-shell">
      <Seo title="Jury Chamber · AdAura Awards" description="Private area — not indexed." path="/" noindex />
      {/* Side rail — dark brown editorial contrast */}
      <aside className="hidden md:flex flex-col w-64 border-r border-[#B8884A]/30 bg-[#2D1810] text-white">
        <div className="p-7 border-b border-[#B8884A]/25">
          <div className="flex items-center gap-3">
            <img src="/adaura-nav-mark.png" alt="AdAura" className="h-9 w-auto object-contain drop-shadow-[0_2px_6px_rgba(217,178,106,0.25)]" />
            <div>
              <div className="text-[10px] tracking-[0.4em] uppercase text-[#D9B26A]">AdAura</div>
              <div className="font-[Libre_Baskerville] text-lg text-white leading-tight">Jury Chamber</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-5 space-y-1">
          <RailLink to="/jury-chamber" icon={ScrollText} label="Dashboard" exact active={active === 'dashboard'} />
          <RailLink to="/jury-chamber/entries" icon={Tags} label="Assigned Entries" active={active === 'entries'} />
        </nav>
        <div className="p-5 border-t border-[#B8884A]/25">
          <div className="text-white text-sm" data-testid="jury-user-name">{juror?.name}</div>
          <div className="text-white/55 text-[10px] tracking-[0.2em] uppercase mt-0.5">{juror?.title || 'Juror'}</div>
          {juror?.company && <div className="text-white/55 text-[10px] mt-0.5">{juror.company}</div>}
          <button data-testid="jury-logout-btn" onClick={doLogout} className="mt-4 inline-flex items-center gap-2 text-white/55 hover:text-[#D9B26A] text-[10px] tracking-[0.3em] uppercase transition-colors">
            <LogOut size={12} /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 border-b border-[#B8884A]/25 bg-[#2D1810]">
        <div className="px-5 py-3 flex items-center justify-between">
          <Link to="/jury-chamber" className="flex items-center gap-2.5">
            <img src="/adaura-nav-mark.png" alt="AdAura" className="h-7 w-auto object-contain" />
            <div>
              <div className="text-[9px] tracking-[0.4em] uppercase text-[#D9B26A]">AdAura</div>
              <div className="font-[Libre_Baskerville] text-base text-white leading-tight">Jury Chamber</div>
            </div>
          </Link>
          <button data-testid="jury-logout-btn-mobile" onClick={doLogout} className="text-white/65"><LogOut size={16} /></button>
        </div>
        <div className="px-5 pb-2 flex gap-4 text-[10px] tracking-[0.3em] uppercase">
          <NavLink to="/jury-chamber" end className={({ isActive }) => isActive ? 'text-[#D9B26A] border-b-2 border-[#D9B26A] pb-1' : 'text-white/55 pb-1'}>Dashboard</NavLink>
          <NavLink to="/jury-chamber/entries" className={({ isActive }) => isActive ? 'text-[#D9B26A] border-b-2 border-[#D9B26A] pb-1' : 'text-white/55 pb-1'}>Entries</NavLink>
        </div>
      </div>

      <main className="flex-1 md:pl-0 pt-24 md:pt-0">{children}</main>
    </div>
  );
};

const RailLink = ({ to, icon: Icon, label, exact, active }) => (
  <NavLink to={to} end={exact} className={({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 text-xs tracking-[0.25em] uppercase transition-colors ${isActive || active ? 'bg-[#D9B26A]/15 text-[#D9B26A] border-l-2 border-[#D9B26A]' : 'text-white/65 hover:text-white border-l-2 border-transparent'}`
  }>
    <Icon size={14} /> {label}
  </NavLink>
);

const JuryDashboard = () => {
  usePageTitle('Jury Chamber');
  const { juror } = useJuryAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data: d } = await ax.get(`${API}/jury/dashboard`);
      setData(d);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Could not load dashboard.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <JuryShell active="dashboard"><div className="p-12 text-[var(--ink-muted)] tracking-[0.3em] uppercase text-xs">Loading…</div></JuryShell>;
  }

  const pct = fmtPercent(data?.entries_completed, data?.entries_total);

  return (
    <JuryShell active="dashboard">
      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-12 lg:py-16" data-testid="jury-dashboard">
        <div className="text-[10px] tracking-[0.5em] uppercase text-[var(--brand-brass)] mb-3">Welcome to the Chamber</div>
        <h1 className="font-[Libre_Baskerville] text-4xl md:text-5xl text-[var(--ink)] leading-tight">
          {juror?.name?.split(' ')[0] || 'Juror'}<span className="text-[var(--brand-brass)]">.</span>
        </h1>
        <p className="text-[var(--ink-muted)] text-sm mt-4 max-w-xl italic font-serif">
          You are part of an independent jury reviewing India&rsquo;s most distinguished marketing work of the year.
        </p>

        {/* Progress band */}
        <div className="mt-12 border border-[var(--gold-faint)] bg-[var(--bg-alt)]/40 p-8" data-testid="jury-progress">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)]">Evaluation Progress</div>
              <div className="font-[Libre_Baskerville] text-3xl text-[var(--ink)] mt-2">{data?.entries_completed}<span className="text-[var(--ink-muted)] text-2xl"> / {data?.entries_total}</span></div>
            </div>
            <div className="text-right">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)]">Deadline</div>
              <div className="text-[var(--gold)] mt-1 inline-flex items-center gap-2"><Clock size={14} /> {data?.deadline}</div>
            </div>
          </div>
          <div className="mt-5 h-1 bg-[var(--gold-faint)]/30">
            <div className="h-1 bg-[var(--gold)] transition-all duration-700" style={{ width: `${pct}%` }} />
          </div>
          <div className="mt-2 text-[var(--ink-muted)] text-[10px] tracking-[0.3em] uppercase">{pct}% complete</div>
        </div>

        {/* Stat row */}
        <div className="grid grid-cols-3 gap-px bg-[var(--gold-faint)]/25 mt-8">
          <StatCell label="Assigned Categories" value={data?.assigned_categories?.length || 0} testId="jury-stat-categories" />
          <StatCell label="Entries Pending" value={data?.entries_pending ?? 0} accent="#E0A659" testId="jury-stat-pending" Icon={Circle} />
          <StatCell label="Entries Completed" value={data?.entries_completed ?? 0} accent="#7BC47F" testId="jury-stat-completed" Icon={CheckCircle2} />
        </div>

        {/* Assigned categories */}
        <div className="mt-12">
          <h2 className="font-[Libre_Baskerville] text-2xl text-[var(--ink)] mb-5">Your Assigned Categories</h2>
          {data?.assigned_categories?.length ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--gold-faint)]/25" data-testid="jury-categories-list">
              {data.assigned_categories.map((c) => (
                <div key={c} className="bg-[var(--bg)] p-5">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--gold)] mb-1">Category</div>
                  <div className="text-[var(--ink)] text-sm leading-snug">{c}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="border border-dashed border-[var(--gold-faint)] p-8 text-center text-[var(--ink-muted)] text-sm" data-testid="jury-categories-empty">
              No categories assigned yet. The awards team will assign your panel shortly.
            </div>
          )}
        </div>

        <div className="mt-12 flex justify-end">
          <Link to="/jury-chamber/entries" data-testid="jury-go-to-entries" className="inline-flex items-center gap-2 bg-[var(--gold)] hover:bg-[var(--gold-bright)] text-[var(--bg)] tracking-[0.25em] uppercase text-xs px-6 py-3 transition-colors">
            Begin Review <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </JuryShell>
  );
};

const StatCell = ({ label, value, accent, testId, Icon }) => (
  <div className="bg-[var(--bg)] p-6" data-testid={testId}>
    <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)]">
      {Icon && <Icon size={12} style={{ color: accent || 'var(--ink-muted)' }} />} {label}
    </div>
    <div className="font-[Libre_Baskerville] text-4xl mt-3" style={{ color: accent || 'var(--gold)' }}>{value}</div>
  </div>
);

export default JuryDashboard;
export { JuryShell };
